import React, { createContext, useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authAPI, wsService, settingsAPI } from './services/api';
import { ThemeProvider } from './ui/hooks/useTheme';
import './App.css';

// Sign-in and the two CLIENT-facing pages. These are the only screens left
// under components/ that this file routes: a client opening an invoice or an
// estimate is not a crew member and never was part of the rebuild.
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import PublicInvoice from './components/PublicInvoice/PublicInvoice';
import PublicEstimate from './components/PublicEstimate/PublicEstimate';
// The client portal (docs/CLIENT_PORTAL.md): signed-in CLIENTS, on their own
// cookie. Outside ProtectedRoute like the two public pages above, and never
// sent a staff token (apps/portal/portalApi.js).
import PortalApp from './apps/portal/PortalApp';

// The one app (docs/ONE_APP.md). One route tree, one shell, no cutover.
import AppRouter from './apps/AppRouter';
import { clearFieldDisplayState } from './apps/field/offline/owner';

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Settings Context
const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

// Auth Provider
/**
 * The SAME person's row, updated — the rule behind `updateUser` (#512).
 *
 * Exported and pure so the merge itself is testable. It is the whole fix, and
 * when it lived inline nothing covered it: every screen suite mocks `useAuth`,
 * so replacing the merge with a plain overwrite reddened NOTHING while
 * reintroducing the defect exactly.
 *
 * `patch` wins field by field, but a key the patch does not carry is KEPT —
 * which is the point. `PUT /api/auth/profile` answers without `permissions`,
 * and dropping that key is what took every useCan() to false.
 */
export function mergeUser(prev, patch) {
  if (!patch) return prev || null;
  return { ...(prev || {}), ...patch };
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) {
      wsService.connect(token);
    }
    
    return () => {
      wsService.disconnect();
    };
  }, [token]);

  /**
   * Sign out — and take the previous person's DAY off the screen with them.
   *
   * `fw_field_now` (the optimistic running clock) and `fw_field_cache:*` (the
   * cached day bundle) are display state, not work, and they outlive a logout:
   * without this the next person to sign in on a shared crew phone opens Today
   * and sees somebody else's job running. Removed BY NAME —
   * server/utils/__tests__/noLocalStorageClear.test.js bans a blanket clear,
   * because localStorage here also holds the token and the theme.
   *
   * The offline QUEUES are deliberately not cleared: they hold work a crew
   * member watched the app accept, and each record now carries its owner
   * (apps/field/offline/owner.js), so it waits for that person instead of
   * replaying under whoever signs in next.
   */
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearFieldDisplayState();
    setToken(null);
    setUser(null);
    wsService.disconnect();
  };

  const fetchUser = async () => {
    try {
      const { data } = await authAPI.getMe();
      setUser(data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(token);
    setUser(userData);
  };

  /**
   * The SAME person's row changed — merge it, do not replace them (#512).
   *
   * `PUT /api/auth/profile` answers with a users row that carries no
   * `permissions` key, while `GET /auth/me` deliberately adds one. Profile
   * saves fed that answer straight into `login`, which REPLACES the user, so
   * `user.permissions` vanished and every `useCan()` went false for the rest
   * of the session. A manager who saved their phone number could no longer
   * create or edit a job. Admins were exempt — `useCan` short-circuits on the
   * role — which is why it survived: it is invisible to whoever tests.
   *
   * Nothing re-hydrated afterwards either: `fetchUser()` is keyed on the token
   * in a useEffect, and the token handed back is the same string already in
   * state, so React bails out and `/auth/me` is never re-called.
   *
   * Deliberately NOT done by making `login` merge. `login` means "this is who
   * is signed in now", and merging there would let a genuine sign-in as a
   * different person inherit the previous person's permissions — trading a
   * missing-capability bug for an extra-capability one.
   */
  const updateUser = (patch) => {
    if (!patch) return;
    setUser((prev) => {
      const merged = mergeUser(prev, patch);
      try { localStorage.setItem('user', JSON.stringify(merged)); } catch (_) { /* private mode */ }
      return merged;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, updateUser, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

// Settings Provider
function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    brand_name: 'Full Circle Property',
    brand_short: 'FCP',
    brand_color: '#2563eb',
    brand_logo: '',
    appearance_preset: 'default',
  });
  const [loading, setLoading] = useState(false); // Changed to false by default

  useEffect(() => {
    fetchSettings();
  }, []);

  // Before login we can only read the public branding allow-list; the full
  // settings table is authenticated (it holds smtp_password and API keys, and
  // used to be readable by anyone on the internet — see server/routes/settings.js).
  //
  // /public returns rows [{key, value}]; the authenticated route returns a flat
  // object. Normalise so SettingsContext has one shape either way.
  const fetchSettings = async () => {
    const hasToken = Boolean(localStorage.getItem('token'));
    try {
      if (hasToken) {
        const { data } = await settingsAPI.getAll();
        setSettings(data);
      } else {
        const { data } = await settingsAPI.getPublic();
        const obj = Array.isArray(data)
          ? Object.fromEntries(data.map(r => [r.key, r.value]))
          : (data || {});
        setSettings(obj);
      }
    } catch (error) {
      console.warn('Failed to fetch settings, using defaults:', error.message);
      // Don't crash - just use default settings
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, refetch: fetchSettings }}>
      <ThemedShell>{children}</ThemedShell>
    </SettingsContext.Provider>
  );
}

// ThemedShell — wraps children in ThemeProvider, pulling brand color and
// appearance preset out of SettingsContext. Lives between SettingsProvider
// and Router so every page gets theme tokens applied.
function ThemedShell({ children }) {
  const { settings } = useSettings();
  return (
    <ThemeProvider
      brandColor={settings?.brand_color}
      preset={settings?.appearance_preset || 'default'}
    >
      {children}
    </ThemeProvider>
  );
}

// Protected Route
function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Layout Component
// Layout lived here — 747 lines of classic chrome: the desktop nav, MobileNav,
// the version bar and the service-worker update toast. Deleted with the
// cutover (docs/ONE_APP.md).
//
// One piece of it was NOT dropped, because it is the mechanism behind
// "everyone gets the latest when they open the app": the service-worker
// update detection now lives in apps/shell/UpdateGate.js and is mounted by
// the one shell, so it covers every screen instead of only the classic ones.

// Main App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider>
          <Router>
            {/* PWAInstallPrompt is rendered by ClassicRoutes, NOT here.
                Above <Routes> it has no route and no auth to gate on, so it
                appeared on /login and — measured at 390x844 with an iOS user
                agent — on the public /estimate/:token page a CLIENT opens to
                read a quote, offering to install the crew's app. It also
                stacked with the rebuild's own <InstallApp />, so Field → Me
                showed two install offers at once. */}
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/invoice/:token" element={<PublicInvoice />} />
              <Route path="/estimate/:token" element={<PublicEstimate />} />
              <Route path="/portal/*" element={<PortalApp />} />

              {/* Everything else IS the app. There is no cutover gate and no
                  /classic any more (docs/ONE_APP.md): one route tree, one
                  shell, and no way back to an older one — because there isn't
                  an older one. ProtectedRoute still guards it; a signed-out
                  person lands on /login exactly as before. */}
              <Route path="/*" element={<ProtectedRoute><AppRouter /></ProtectedRoute>} />
            </Routes>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// The classic application — the 25 screens the rebuild replaces. Unchanged;
// it keeps running for any role not yet moved over (REBUILD_SPEC §9).
// ClassicRoutes lived here — 174 lines routing the 25 screens under
// client/src/components/. Deleted with the cutover (docs/ONE_APP.md): there
// is one app now, so there is no second route tree to fall back to and no
// /classic to reach it with. The public client-facing pages (PublicInvoice,
// PublicEstimate) are still routed above — those are for clients, not crew,
// and were never part of the rebuild.

export default App;
